/**
 * waap-aiwaf-advanced-tab.tsx — AI-WAF Advanced Protection tab (B4 batch).
 *
 * Lists advanced strategies per type (Anti-Leech, Brute Force, etc.) in a
 * tab-per-type layout. Each type panel supports create / edit / delete.
 *
 * Endpoints (anchor-scoped):
 *   GET  /waap/domains/:anchorId/advanced-strategies?type=N
 *   POST /waap/domains/:anchorId/advanced-strategies
 *   PUT  /waap/domains/:anchorId/advanced-strategies/:sid
 *   DEL  /waap/domains/:anchorId/advanced-strategies/:sid
 *
 * Body: { type, name, description, sites: number[], config: Record<string,unknown> }
 * All mutations via ActAsMutationConfirm.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  Card, CardBody,
  Button, IconButton, Input, Textarea, Badge, Skeleton,
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { TabError } from '@/pages/customers/detail/tab-states';
import { ActAsMutationConfirm } from '@/pages/operations/detail/cdn/act-as-mutation-confirm';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';
import {
  ADVANCED_TYPES,
  ADVANCED_TYPE_LABELS,
  ADVANCED_TYPE_VALUES,
  useActAsWAAPAdvancedStrategies,
  type AdvancedStrategyListItem,
} from '@/api/act-as-waap-rules';

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function parseIdList(text: string): number[] {
  return text
    .split(/[\s,]+/)
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
}

function formatIdList(ids: number[] | undefined): string {
  return (ids ?? []).join(', ');
}

const EMPTY_RULE: AdvancedStrategyListItem = {
  name: '',
  type: ADVANCED_TYPES.ANTI_LEECH,
  description: '',
  sites: [],
  config: {},
};

interface RulePending {
  kind: 'create' | 'update' | 'delete';
  rule: AdvancedStrategyListItem;
}

/* ── Per-type panel ──────────────────────────────────────────────────────── */

function AdvancedTypePanel({
  anchorId,
  type,
}: {
  anchorId: string;
  type: number;
}) {
  const { t } = useTranslation();
  const rulesQ = useActAsWAAPAdvancedStrategies(anchorId, type);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing]   = useState<AdvancedStrategyListItem | null>(null);
  const [draft, setDraft]       = useState<AdvancedStrategyListItem>({ ...EMPTY_RULE, type });
  const [sitesDraft, setSitesDraft] = useState('');
  const [configDraft, setConfigDraft] = useState('{}');
  const [pending, setPending]   = useState<RulePending | null>(null);

  function openNew() {
    setEditing(null);
    setDraft({ ...EMPTY_RULE, type });
    setSitesDraft('');
    setConfigDraft('{}');
    setFormOpen(true);
  }

  function openEdit(rule: AdvancedStrategyListItem) {
    setEditing(rule);
    setDraft({ ...rule });
    setSitesDraft(formatIdList(rule.sites));
    setConfigDraft(JSON.stringify(rule.config ?? {}, null, 2));
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  function parseConfig(): Record<string, unknown> {
    try { return JSON.parse(configDraft) as Record<string, unknown>; }
    catch { return {}; }
  }

  function handleSubmit() {
    const payload: AdvancedStrategyListItem = {
      ...draft,
      sites: parseIdList(sitesDraft),
      config: parseConfig(),
    };
    setPending({ kind: editing ? 'update' : 'create', rule: payload });
  }

  function buildMutationFn(p: RulePending) {
    const base = `/waap/domains/${anchorId}/advanced-strategies`;
    if (p.kind === 'create') return () => actAsClient.post(base, p.rule);
    if (p.kind === 'update') return () => actAsClient.put(`${base}/${p.rule.id}`, p.rule);
    return () => actAsClient.delete(`${base}/${p.rule.id}`);
  }

  function patch(key: keyof AdvancedStrategyListItem, value: unknown) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  const isValid = draft.name.trim() !== '';

  if (rulesQ.isLoading) return <Skeleton style={{ width: '100%', height: 80 }} />;
  if (rulesQ.isError) {
    return (
      <TabError
        title={t('waap_domain.advanced_load_error', 'Failed to load advanced rules')}
        onRetry={() => rulesQ.refetch()}
      />
    );
  }

  const rules = rulesQ.data ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 600 }}>
          <Badge style={{ marginRight: 'var(--vn-space-2)' }}>{rules.length}</Badge>
          {t('waap_domain.advanced_rules_count', '{{count}} rules', { count: rules.length })}
        </span>
        <Button size="sm" variant="secondary" onClick={openNew}>
          <Plus size={13} />
          {t('waap_domain.advanced_add', 'Add rule')}
        </Button>
      </div>

      {rules.length === 0 && !formOpen ? (
        <EmptyState
          title={t('waap_domain.advanced_empty', 'No rules for this type')}
          description={t('waap_domain.advanced_empty_desc', 'Create a rule to activate this advanced protection strategy.')}
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {rules.map((rule) => (
            <Card key={rule.id ?? rule.name}>
              <CardBody style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rule.name}
                  </div>
                  {rule.description && (
                    <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 2 }}>
                      {rule.description}
                    </div>
                  )}
                  {(rule.sites?.length ?? 0) > 0 && (
                    <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                      {t('waap_domain.advanced_sites', 'Sites: {{ids}}', { ids: rule.sites!.join(', ') })}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
                  <IconButton size="sm" variant="ghost" aria-label={t('common.edit', 'Edit')} onClick={() => openEdit(rule)}>
                    <Pencil size={13} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label={t('common.delete', 'Delete')}
                    onClick={() => setPending({ kind: 'delete', rule })}
                  >
                    <Trash2 size={13} />
                  </IconButton>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {formOpen && (
        <Card>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
              {editing
                ? t('waap_domain.advanced_edit', 'Edit rule')
                : t('waap_domain.advanced_new', 'New rule')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--vn-space-3)' }}>
              <Field label={t('waap_domain.advanced_field_name', 'Name')}>
                {(p) => (
                  <Input {...p} value={draft.name} onChange={(e) => patch('name', e.target.value)} placeholder="Rule name" />
                )}
              </Field>
              <Field label={t('waap_domain.advanced_field_desc', 'Description')}>
                {(p) => (
                  <Input {...p} value={draft.description ?? ''} onChange={(e) => patch('description', e.target.value)} placeholder="Optional" />
                )}
              </Field>
              <Field label={t('waap_domain.advanced_field_sites', 'Site IDs (comma-sep)')}>
                {(p) => (
                  <Input {...p} value={sitesDraft} onChange={(e) => setSitesDraft(e.target.value)} placeholder="1, 2, 3" />
                )}
              </Field>
            </div>
            <Field
              label={t('waap_domain.advanced_field_config', 'Config (JSON)')}
              description={t('waap_domain.advanced_field_config_desc', 'Per-type configuration object. See vendor docs for the correct shape.')}
            >
              {(p) => (
                <Textarea
                  {...p}
                  value={configDraft}
                  onChange={(e) => setConfigDraft(e.target.value)}
                  rows={5}
                  style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)' }}
                />
              )}
            </Field>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="secondary" onClick={closeForm}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button size="sm" variant="primary" disabled={!isValid} onClick={handleSubmit}>
                {editing ? t('common.save', 'Save') : t('common.create', 'Create')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {pending && (
        <ActAsMutationConfirm
          open
          onOpenChange={(open) => { if (!open) setPending(null); }}
          title={
            pending.kind === 'delete'
              ? t('waap_domain.advanced_delete_title', 'Delete rule')
              : pending.kind === 'create'
              ? t('waap_domain.advanced_create_title', 'Create rule')
              : t('waap_domain.advanced_update_title', 'Update rule')
          }
          summary={
            pending.kind === 'delete'
              ? t('waap_domain.advanced_delete_summary', 'Permanently delete rule "{{name}}".', { name: pending.rule.name })
              : t('waap_domain.advanced_save_summary', 'Save advanced rule "{{name}}".', { name: pending.rule.name })
          }
          destructive={pending.kind === 'delete'}
          queryKeysToInvalidate={[
            [...actAsWAAPKeys.aspect(anchorId, 'advanced-strategies'), type],
          ]}
          mutationFn={buildMutationFn(pending)}
          onSuccess={() => { setPending(null); closeForm(); rulesQ.refetch(); }}
        />
      )}
    </div>
  );
}

/* ── Root component ──────────────────────────────────────────────────────── */

interface WaapAiwafAdvancedTabProps {
  anchorId: string;
}

/**
 * AI-WAF Advanced Protection tab — 9 strategy types, each in its own sub-tab.
 * Create / edit / delete per type via ActAsMutationConfirm.
 */
export function WaapAiwafAdvancedTab({ anchorId }: WaapAiwafAdvancedTabProps) {
  const [activeType, setActiveType] = useState<string>(String(ADVANCED_TYPE_VALUES[0]));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList style={{ flexWrap: 'wrap' }}>
          {ADVANCED_TYPE_VALUES.map((type) => (
            <TabsTrigger key={type} value={String(type)}>
              {ADVANCED_TYPE_LABELS[type]}
            </TabsTrigger>
          ))}
        </TabsList>

        {ADVANCED_TYPE_VALUES.map((type) => (
          <TabsContent key={type} value={String(type)}>
            {activeType === String(type) && (
              <AdvancedTypePanel anchorId={anchorId} type={type} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
